import * as React from 'react';
import Tab from '@mui/material/Tab';
import TabList from '@mui/lab/TabList';
import styled from 'styled-components';

import { useTheme } from '../../styles/theme';

const Container = styled.div<{
  color?: string;
  flush?: boolean;
  center?: boolean;
  noDivider?: boolean;
  queueSize: number;
  useQueue: boolean;
  inset?: boolean;
  insetValue?: string;
}>`
  margin: ${({ flush }) => (flush ? 0 : '0 1rem')};
  padding: ${({ flush, inset = true, insetValue }) => (!flush || !inset ? 0 : `0 ${insetValue || '1.25rem'}`)};
  border-bottom: ${({ theme, noDivider }) => (noDivider ? 0 : `1px solid ${theme.line.light}`)};
  font-family: 'PPFormula';

  display: flex;
  align-items: flex-end;
  justify-content: ${({ center }) => (center ? 'center' : 'flex-start')};

  & .Mui-selected {
    color: ${({ theme, color }) => (color ? color : theme.telnyx.green)} !important;
  }

  & .MuiTabs-root {
    max-height: ${({ flush }) => (flush ? '40px' : '50px')};
    min-height: ${({ flush }) => (flush ? '40px' : '50px')};
  }

  & .MuiButtonBase-root {
    min-width: 0px !important;
  }

  & .MuiTabs-flexContainer {
    gap: 1rem;
  }

  & .MuiTab-root {
    display: flex;
    font-family: 'PPFormula';
    padding: 0 0px;

    grid-gap: 0.5rem;
    max-height: ${({ flush }) => (flush ? '44px' : '60px')};
    min-height: ${({ flush }) => (flush ? '44px' : '60px')};
    font-size: 10px !important;

    &:last-of-type {
      // sidebar size - first 3 tabs size - margin
      margin-left: ${({ useQueue, queueSize }) => (useQueue ? `calc(400px - 240px - 20px - ${queueSize}px)` : 0)};
    }
  }

  & .MuiTabs-indicator {
    background-color: ${({ theme, color }) => (color ? color : theme.telnyx.green)} !important;
  }
`;

type Tab = {
  label: string;
  value: string;
  disabled?: boolean;
};

interface Props {
  tabs: Tab[];
  setTab: React.Dispatch<string>;
  flush?: boolean;
  background?: string | null;
  center?: boolean;
  noDivider?: boolean;
  queueSize: number | false;
  insetTabs?: boolean;
  insetValue?: string;
}

export const Tabs: React.FC<Props> = ({
  tabs,
  setTab,
  flush = false,
  background = null,
  center,
  noDivider,
  queueSize,
  insetTabs = true,
  insetValue,
}) => {
  const theme = useTheme();

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setTab(newValue);
  };

  const getQueueSize = () => {
    if (!queueSize) return 0;

    if (queueSize === 0) return 0;
    if (queueSize > 0 && queueSize <= 9) return 16;
    if (queueSize >= 10 && queueSize <= 99) return 21;
    if (queueSize >= 100 && queueSize <= 999) return 29;
    if (queueSize >= 1000 && queueSize <= 9999) return 37;
    return 45;
  };

  return (
    <Container
      color={background || theme.primary}
      flush={flush}
      center={center}
      noDivider={noDivider}
      inset={insetTabs}
      insetValue={insetValue}
      queueSize={getQueueSize()}
      useQueue={queueSize !== undefined && tabs.length === 4}
    >
      <TabList onChange={handleChange}>
        {tabs.map((tab, index) => (
          <Tab
            key={tab.value}
            disableRipple
            label={
              Boolean(index === tabs.length - 1 && queueSize && queueSize > 0)
                ? `${tab.label} (${queueSize})`
                : tab.label
            }
            value={tab.value}
            disabled={tab?.disabled}
          />
        ))}
      </TabList>
    </Container>
  );
};
